import { fetchWithRetry, fetch_request } from '../service/request';
import { getMlsEntryCacheBustQuery } from './mlsCacheBuster.js';
import { prepareMlsMessagePayload } from './mlsMessagePayload.js';
import { randomUuid, bytesToBase64 } from './utils.js';

const DEFAULT_MLS_PROBE_EPOCH = 0;
const DEFAULT_MLS_PROBE_CIPHERSUITE = 0x004d;

const inflightConversationGroups = new Map();
const cachedConversationGroups = new Map();
const SNAPSHOT_TTL_MS = 60_000;

let keyPackagesInflight = null;

const sleep = (ms) =>
  ms > 0 ? new Promise((resolve) => setTimeout(resolve, ms)) : Promise.resolve();

const parseJson = async (response, fallbackMessage) => {
  let payload = null;
  try {
    payload = await response.json();
  } catch {}

  if (response.ok) {
    return payload;
  }

  const error = new Error(payload?.message || fallbackMessage || `HTTP ${response.status}`);
  error.status = response.status;
  error.payload = payload;
  throw error;
};

const parseMlsApiError = async (response, fallbackMessage) => {
  let payload = null;
  const contentType = response.headers?.get?.('content-type') || '';
  if (contentType.includes('application/json')) {
    try {
      payload = await response.json();
    } catch {}
  } else {
    try {
      payload = await response.text();
    } catch {}
  }

  const messageCandidate =
    (payload && typeof payload === 'object' && (payload.error?.message || payload.message)) ||
    (typeof payload === 'string' && payload) ||
    fallbackMessage ||
    `HTTP ${response.status}`;

  const error = new Error(messageCandidate);
  error.status = response.status;
  if (typeof payload === 'string') {
    error.responseText = payload;
  }

  if (payload && typeof payload === 'object') {
    const code = payload.error?.code || payload.code || null;
    if (code) {
      error.code = code;
    }
    error.payload = payload;
    if (!error.responseText) {
      try {
        error.responseText = JSON.stringify(payload);
      } catch {}
    }
  } else {
    error.payload = payload;
  }

  return error;
};

const coerceEnvelopeB64 = (value) => {
  if (!value) {
    return null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }

  if (value instanceof Uint8Array || ArrayBuffer.isView(value)) {
    const view = value instanceof Uint8Array
      ? value
      : new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
    return bytesToBase64(view);
  }

  if (value instanceof ArrayBuffer) {
    return bytesToBase64(new Uint8Array(value));
  }

  if (typeof value === 'object') {
    const nested =
      value.envelopeB64
      ?? value.envelope_b64
      ?? value.probeEnvelopeB64
      ?? value.probe_envelope_b64
      ?? value.publicMessageB64
      ?? value.public_message_b64
      ?? value.bytes
      ?? value.buffer
      ?? value.envelopeBytes
      ?? value.envelope_bytes
      ?? null;
    if (nested) {
      return coerceEnvelopeB64(nested);
    }
  }

  return null;
};

const resolveMlsHeaderEpoch = (epoch) => {
  if (epoch == null || epoch === '') {
    return null;
  }
  if (typeof epoch === 'number' && Number.isFinite(epoch)) {
    return String(epoch);
  }
  if (typeof epoch === 'string') {
    const trimmed = epoch.trim();
    if (trimmed) {
      return trimmed;
    }
    return null;
  }
  const numericEpoch = Number.parseInt(epoch, 10);
  return Number.isFinite(numericEpoch) ? String(numericEpoch) : null;
};

const resolveMlsHeaderCiphersuite = (ciphersuite) => {
  if (ciphersuite == null || ciphersuite === '') {
    return null;
  }
  if (typeof ciphersuite === 'number' && Number.isFinite(ciphersuite)) {
    return String(ciphersuite);
  }
  if (typeof ciphersuite === 'string') {
    const trimmed = ciphersuite.trim();
    if (trimmed) {
      return trimmed;
    }
    return null;
  }
  const numericSuite = Number.parseInt(ciphersuite, 10);
  return Number.isFinite(numericSuite) ? String(numericSuite) : null;
};

const resolveMlsHeaderValues = ({ groupId, epoch, ciphersuite }) => ({
  groupId: String(groupId),
  epoch: resolveMlsHeaderEpoch(epoch),
  ciphersuite: resolveMlsHeaderCiphersuite(ciphersuite),
});

export const fetchUserKeyBundles = async (userId) => {
  if (!userId) return { devices: [] };
  const response = await fetch_request(`/e2ee/v1/users/${userId}/keys`);
  return parseJson(response, 'Failed to fetch user device keys');
};

const normalizeDevicePayload = (device = {}) => {
  const payload = { ...device };

  if (payload.last_seen_at instanceof Date) {
    payload.last_seen_at = payload.last_seen_at.toISOString();
  }

  Object.keys(payload).forEach((key) => {
    if (payload[key] == null) {
      delete payload[key];
    }
  });

  return { device: payload };
};

export const listRegisteredDevices = async () => {
  const response = await fetch_request('/e2ee/v1/devices');
  return parseJson(response, 'Failed to fetch registered devices');
};

export const registerDevice = async (device) => {
  const body = normalizeDevicePayload(device);
  const response = await fetch_request('/e2ee/v1/devices', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  return parseJson(response, 'Failed to register device');
};

export const updateRegisteredDevice = async ({ deviceId, ...device }) => {
  if (!deviceId) {
    throw new Error('deviceId is required to update a device');
  }

  const body = normalizeDevicePayload(device);
  const response = await fetch_request(`/e2ee/v1/devices/${deviceId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  return parseJson(response, 'Failed to update device');
};

export const deleteRegisteredDevice = async ({ deviceId }) => {
  if (!deviceId) {
    throw new Error('deviceId is required to delete a device');
  }

  const response = await fetch_request(`/e2ee/v1/devices/${deviceId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    await parseJson(response, 'Failed to delete device');
  }

  return true;
};

export const sendEncryptedMessage = async ({
  conversationId,
  senderDeviceId,
  envelopes,
  version = 1,
  idempotencyKey,
}) => {
  if (!conversationId) {
    throw new Error('conversationId is required to send encrypted message');
  }
  if (!senderDeviceId) {
    throw new Error('senderDeviceId is required to send encrypted message');
  }
  if (!Array.isArray(envelopes) || envelopes.length === 0) {
    throw new Error('envelopes must contain at least one entry');
  }

  const key = idempotencyKey || randomUuid();

  const response = await fetch_request('/e2ee/v1/messages', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Idempotency-Key': key,
    },
    body: JSON.stringify({
      message: {
        conversation_id: conversationId,
        sender_device_id: senderDeviceId,
        version,
        envelopes,
      },
    }),
  });

  const payload = await parseJson(response, 'Failed to send encrypted message');
  return { ...payload, idempotencyKey: key };
};

export const fetchEncryptedMessagesSince = async ({ conversationId, after = 0 } = {}) => {
  if (!conversationId) {
    throw new Error('conversationId is required to fetch encrypted messages');
  }

  const params = new URLSearchParams();
  params.set('conversation_id', conversationId);
  if (after) params.set('after', after);

  const response = await fetch_request(`/e2ee/v1/messages/since?${params.toString()}`);
  return parseJson(response, 'Failed to fetch encrypted messages');
};

export const acknowledgePreKey = async ({ deviceId, opkId, status = 'consumed' }) => {
  if (!deviceId || !opkId) return null;

  const response = await fetch_request('/e2ee/v1/prekeys/ack', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ack: { device_id: deviceId, opk_id: opkId, status } }),
  });

  return parseJson(response, 'Failed to acknowledge prekey');
};

const normalizePendingWelcomeEntry = (entry) => {
  if (!entry || typeof entry !== 'object') {
    return null;
  }

  const pickString = (...candidates) => {
    for (const candidate of candidates) {
      if (candidate == null) continue;
      try {
        const normalized = String(candidate);
        if (normalized.trim()) {
          return normalized;
        }
      } catch {}
    }
    return null;
  };

  const normalizeBoolean = (value) => {
    if (typeof value === 'boolean') {
      return value;
    }
    if (value === 'true') {
      return true;
    }
    if (value === 'false') {
      return false;
    }
    return undefined;
  };

  const normalized = { ...entry };

  const welcomeId = pickString(entry.welcome_id, entry.welcomeId, entry.id);
  if (welcomeId != null) {
    normalized.welcome_id = welcomeId;
    normalized.welcomeId = welcomeId;
  }

  const welcomeB64 = pickString(
    entry.welcome_b64,
    entry.welcomeB64,
    typeof entry.welcome === 'string' ? entry.welcome : null,
  );
  if (welcomeB64 != null) {
    normalized.welcome_b64 = welcomeB64;
    normalized.welcomeB64 = welcomeB64;
  }

  const ratchetTreeB64 = pickString(
    entry.ratchet_tree_b64,
    entry.ratchetTreeB64,
    entry.ratchet_tree,
    entry.ratchetTree,
  );
  if (ratchetTreeB64 != null) {
    normalized.ratchet_tree_b64 = ratchetTreeB64;
    normalized.ratchetTreeB64 = ratchetTreeB64;
  }

  const groupId = pickString(entry.group_id, entry.groupId);
  if (groupId != null) {
    normalized.group_id = groupId;
    normalized.groupId = groupId;
  }

  const clientId = pickString(
    entry.identity_id,
    entry.identityId,
    entry.client_id,
    entry.clientId,
    entry.to_client_id,
    entry.toClientId,
    entry.target_client_id,
    entry.targetClientId,
  );
  if (clientId != null) {
    normalized.identity_id = clientId;
    normalized.identityId = clientId;
    normalized.client_id = clientId;
    normalized.clientId = clientId;
    normalized.to_client_id = clientId;
    normalized.toClientId = clientId;
  }

  const epochCandidate = entry.epoch ?? entry.current_epoch ?? entry.currentEpoch;
  if (epochCandidate != null) {
    const numeric = Number(epochCandidate);
    if (Number.isFinite(numeric)) {
      normalized.epoch = numeric;
      normalized.current_epoch = numeric;
      normalized.currentEpoch = numeric;
    }
  }

  const normalizeHexRef = (value) => {
    if (value == null) return null;
    try {
      const normalized = String(value).trim().toLowerCase();
      if (!normalized || !/^[0-9a-f]+$/.test(normalized)) {
        return null;
      }
      return normalized;
    } catch {
      return null;
    }
  };

  const kpRef = normalizeHexRef(
    entry.kp_ref ??
      entry.kpRef ??
      entry.key_package_ref ??
      entry.keyPackageRef ??
      entry.ref ??
      null,
  );
  if (kpRef != null) {
    normalized.kp_ref = kpRef;
    normalized.kpRef = kpRef;
    normalized.key_package_ref = kpRef;
    normalized.keyPackageRef = kpRef;
    normalized.ref = kpRef;
  }

  const usedKpRef = normalizeHexRef(entry.used_kp_ref ?? entry.usedKpRef ?? null);
  if (usedKpRef != null) {
    normalized.used_kp_ref = usedKpRef;
    normalized.usedKpRef = usedKpRef;
  }

  const seqCandidate = entry.seq_no ?? entry.seqNo;
  if (seqCandidate != null) {
    const numericSeq = Number(seqCandidate);
    if (Number.isFinite(numericSeq)) {
      normalized.seq_no = numericSeq;
      normalized.seqNo = numericSeq;
    }
  }

  const handshakeB64 = pickString(
    entry.ciphertext_handshake_b64,
    entry.ciphertextHandshakeB64,
    entry.envelope_b64,
    entry.envelopeB64,
    entry.welcome_b64,
    entry.welcomeB64,
  );
  if (handshakeB64 != null) {
    normalized.ciphertext_handshake_b64 = handshakeB64;
    normalized.ciphertextHandshakeB64 = handshakeB64;
  }

  const handshakeType = pickString(entry.handshake_type, entry.handshakeType, entry.type);
  if (handshakeType != null) {
    normalized.handshake_type = handshakeType;
    normalized.handshakeType = handshakeType;
  }

  const shouldAutoAck = normalizeBoolean(entry.auto_ack ?? entry.shouldAck ?? null);
  if (shouldAutoAck !== undefined) {
    normalized.auto_ack = shouldAutoAck;
    normalized.autoAck = shouldAutoAck;
  }

  return normalized;
};

const normalizeServerGroupSnapshot = (payload = {}) => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const groupPayload = payload.group && typeof payload.group === 'object' ? payload.group : payload;
  if (!groupPayload || typeof groupPayload !== 'object') {
    return null;
  }

  const normalized = { ...groupPayload };

  const normalizeList = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    return [value];
  };

  const pending = normalizeList(
    groupPayload.pending_welcomes ?? groupPayload.pendingWelcomes ?? groupPayload.pending_welcome,
  )
    .map((entry) => normalizePendingWelcomeEntry(entry))
    .filter(Boolean);

  normalized.pending_welcomes = pending;
  normalized.pendingWelcomes = pending;
  const epochCandidate =
    normalized.epoch ?? normalized.current_epoch ?? normalized.currentEpoch ?? null;
  if (epochCandidate != null) {
    const numericEpoch = Number(epochCandidate);
    if (Number.isFinite(numericEpoch)) {
      normalized.epoch = numericEpoch;
      normalized.current_epoch = numericEpoch;
      normalized.currentEpoch = numericEpoch;
    }
  }

  const pendingHandshakeQueue = normalizeList(
    groupPayload.pending_handshake_queue ?? groupPayload.pendingHandshakeQueue,
  )
    .map((entry) => normalizePendingWelcomeEntry(entry))
    .filter(Boolean);
  if (pendingHandshakeQueue.length) {
    normalized.pending_handshake_queue = pendingHandshakeQueue;
    normalized.pendingHandshakeQueue = pendingHandshakeQueue;
  } else if (pending.length) {
    normalized.pending_handshake_queue = pending;
    normalized.pendingHandshakeQueue = pending;
  }

  if (typeof groupPayload.group_state_b64 === 'string' && groupPayload.group_state_b64.trim()) {
    normalized.group_state_b64 = groupPayload.group_state_b64;
    normalized.groupStateB64 = groupPayload.group_state_b64;
    normalized.serializedGroupState = groupPayload.group_state_b64;
  }

  return normalized;
};

export const fetchConversationMlsGroup = async ({ conversationId, clientId, reissue } = {}) => {
  if (!conversationId) {
    throw new Error('conversationId is required to fetch MLS group');
  }

  let endpoint = `/api/v1/conversations/${conversationId}/mls-group`;
  const cacheBustQuery = getMlsEntryCacheBustQuery();
  if (cacheBustQuery) {
    endpoint = `${endpoint}${endpoint.includes('?') ? '&' : '?'}${cacheBustQuery}`;
  }
  if (clientId) {
    const q = `client_id=${encodeURIComponent(String(clientId))}`;
    endpoint = `${endpoint}${endpoint.includes('?') ? '&' : '?'}${q}`;
  }
  if (reissue === true) {
    const q = 'reissue=true';
    endpoint = `${endpoint}${endpoint.includes('?') ? '&' : '?'}${q}`;
  }

  const response = await fetchWithRetry(endpoint, {
    cache: 'no-store',
    headers: {
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      Pragma: 'no-cache',
    },
  });
  const payload = await parseJson(response, 'Failed to fetch MLS group');
  return normalizeServerGroupSnapshot(payload);
};

const resolveConversationKey = (conversationId) => {
  if (conversationId == null) {
    return null;
  }
  try {
    const key = String(conversationId);
    return key.trim() ? key : null;
  } catch {
    return null;
  }
};

const resolveConversationSnapshotCacheKey = (conversationId, clientId, reissue) => {
  const conversationKey = resolveConversationKey(conversationId);
  if (!conversationKey) return null;
  let clientKey = null;
  if (clientId != null) {
    try {
      const normalized = String(clientId).trim();
      clientKey = normalized ? normalized : null;
    } catch {
      clientKey = null;
    }
  }
  const reissueKey = reissue === true ? 'reissue' : 'default';
  return `${conversationKey}::${clientKey || 'default'}::${reissueKey}`;
};

export const resetConversationMlsGroupCache = (conversationId) => {
  if (conversationId == null) {
    inflightConversationGroups.clear();
    cachedConversationGroups.clear();
    return;
  }
  const conversationKey = resolveConversationKey(conversationId);
  if (!conversationKey) {
    return;
  }

  const prefix = `${conversationKey}::`;
  for (const key of inflightConversationGroups.keys()) {
    if (key === conversationKey || String(key).startsWith(prefix)) {
      inflightConversationGroups.delete(key);
    }
  }
  for (const key of cachedConversationGroups.keys()) {
    if (key === conversationKey || String(key).startsWith(prefix)) {
      cachedConversationGroups.delete(key);
    }
  }
};

export const resetConversationMlsGroup = async ({ conversationId } = {}) => {
  if (!conversationId) {
    throw new Error('conversationId is required to reset MLS group');
  }

  const response = await fetchWithRetry(`/api/v1/conversations/${conversationId}/mls/reset`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({}),
  });

  if (response.status === 403 || response.status === 404) {
    // Only available on staging/dev; treat as no-op elsewhere.
    return null;
  }

  if (!response.ok) {
    throw await parseMlsApiError(response, 'Failed to reset MLS conversation group');
  }

  try {
    const payload = await response.json();
    resetConversationMlsGroupCache(conversationId);
    return normalizeServerGroupSnapshot(payload);
  } catch (error) {
    resetConversationMlsGroupCache(conversationId);
    return null;
  }
};

export function fetchConversationMlsGroupOnce({
  conversationId,
  clientId,
  reissue,
  forceNetwork = false,
} = {}) {
  const conversationKey = resolveConversationKey(conversationId);
  if (!conversationKey) {
    throw new Error('conversationId is required');
  }

  const cacheKey = resolveConversationSnapshotCacheKey(conversationKey, clientId, reissue);
  if (!cacheKey) {
    throw new Error('conversationId is required');
  }

  if (!forceNetwork) {
    const existing = inflightConversationGroups.get(cacheKey);
    if (existing) {
      return existing;
    }
  }

  const request = fetchConversationMlsGroup({
    conversationId: conversationKey,
    clientId,
    reissue,
  });
  if (forceNetwork) {
    return request;
  }
  const flight = request.finally(() => {
    inflightConversationGroups.delete(cacheKey);
  });
  inflightConversationGroups.set(cacheKey, flight);
  return flight;
}

export async function getGroupSnapshot({ conversationId, force = false, clientId, reissue } = {}) {
  const conversationKey = resolveConversationKey(conversationId);
  if (!conversationKey) {
    throw new Error('conversationId is required');
  }

  const cacheKey = resolveConversationSnapshotCacheKey(conversationKey, clientId, reissue);
  if (!cacheKey) {
    throw new Error('conversationId is required');
  }

  const shouldForce = force || reissue === true;

  if (!shouldForce) {
    const cached = cachedConversationGroups.get(cacheKey);
    if (cached && Date.now() - cached.ts < SNAPSHOT_TTL_MS) {
      return cached.snapshot;
    }
  } else {
    cachedConversationGroups.delete(cacheKey);
  }

  const snapshot = await fetchConversationMlsGroupOnce({ conversationId: conversationKey, clientId, reissue });
  cachedConversationGroups.set(cacheKey, { snapshot, ts: Date.now() });
  return snapshot;
}

const mintSelfWelcomeFromSnapshot = async ({ groupId, conversationId, identityId }) => {
  const normalizedConversationId = conversationId != null ? String(conversationId) : null;
  const normalizedIdentityId =
    identityId != null && String(identityId).trim() ? String(identityId).trim() : null;

  if (!normalizedConversationId) {
    return undefined;
  }

  let snapshot = null;
  try {
    snapshot = await getGroupSnapshot({
      conversationId: normalizedConversationId,
      force: true,
      clientId: normalizedIdentityId || undefined,
      reissue: true,
    });
  } catch (error) {
    if (error?.status === 404) {
      return undefined;
    }
    throw error;
  }

  if (!snapshot || typeof snapshot !== 'object') {
    return null;
  }

  const resolveObject = (value) => {
    if (!value || typeof value !== 'object') {
      return null;
    }
    return value;
  };

  const pickPendingWelcome = () => {
    const candidates = Array.isArray(snapshot.pending_welcomes)
      ? snapshot.pending_welcomes
      : Array.isArray(snapshot.pendingWelcomes)
        ? snapshot.pendingWelcomes
        : [];
    if (!candidates.length) {
      return null;
    }

    if (normalizedIdentityId) {
      const targeted = candidates.find((entry) => {
        const targetClientId =
          entry?.to_client_id ??
          entry?.toClientId ??
          entry?.identity_id ??
          entry?.identityId ??
          entry?.client_id ??
          entry?.clientId ??
          null;
        return targetClientId != null && String(targetClientId).trim() === normalizedIdentityId;
      });
      if (targeted) {
        return resolveObject(targeted);
      }
    }

    return resolveObject(candidates[0]);
  };

  const welcomeWrapper = resolveObject(snapshot.welcome);
  const pendingWelcome = pickPendingWelcome();
  const payload = welcomeWrapper || pendingWelcome || snapshot;

  const collectString = (candidates) => {
    for (const candidate of candidates) {
      if (candidate == null) continue;
      try {
        const value = String(candidate);
        const trimmed = value.trim();
        if (trimmed) {
          return trimmed;
        }
      } catch {}
    }
    return null;
  };

  const welcomeBuffer = (() => {
    const bufferCandidates = [
      payload.welcome,
      payload.welcome_buffer,
      payload.welcomeBuffer,
    ];
    for (const candidate of bufferCandidates) {
      if (!candidate) continue;
      if (candidate instanceof ArrayBuffer) {
        return candidate;
      }
      if (ArrayBuffer.isView(candidate) && candidate.buffer) {
        return candidate.buffer.slice(candidate.byteOffset, candidate.byteOffset + candidate.byteLength);
      }
    }
    return null;
  })();

  const welcomeB64 = collectString([
    payload.welcome_b64,
    payload.welcomeB64,
    typeof payload.welcome === 'string' ? payload.welcome : null,
    typeof snapshot.welcome_b64 === 'string' ? snapshot.welcome_b64 : null,
    typeof snapshot.welcomeB64 === 'string' ? snapshot.welcomeB64 : null,
    typeof snapshot.welcome === 'string' ? snapshot.welcome : null,
  ]);

  if (!welcomeBuffer && !welcomeB64) {
    return null;
  }

  const ratchetTreeB64 = collectString([
    payload.ratchet_tree_b64,
    payload.ratchetTreeB64,
    payload.ratchet_tree,
    payload.ratchetTree,
    snapshot.ratchet_tree_b64,
    snapshot.ratchetTreeB64,
    snapshot.ratchet_tree,
    snapshot.ratchetTree,
  ]);

  const resolvedGroupId = collectString([
    payload.group_id,
    payload.groupId,
    snapshot.group_id,
    snapshot.groupId,
    groupId,
  ]);

  const resolvedGroupHandle = collectString([
    payload.group_handle,
    payload.groupHandle,
    snapshot.group_handle,
    snapshot.groupHandle,
    resolvedGroupId,
  ]);

  const resolvedEpoch = (() => {
    const candidates = [
      payload.epoch,
      payload.current_epoch,
      payload.currentEpoch,
      snapshot.epoch,
      snapshot.current_epoch,
      snapshot.currentEpoch,
    ];
    for (const candidate of candidates) {
      if (candidate == null) continue;
      const numeric = Number(candidate);
      if (!Number.isNaN(numeric)) {
        return numeric;
      }
    }
    return null;
  })();

  const resolvedMembers = Array.isArray(payload.members)
    ? payload.members
    : Array.isArray(snapshot.members)
    ? snapshot.members
    : null;

  const normalizedWelcome = {
    welcome: welcomeBuffer ?? welcomeB64 ?? null,
    welcome_buffer: welcomeBuffer ?? null,
    welcomeBuffer: welcomeBuffer ?? null,
    welcome_b64: welcomeB64 ?? null,
    welcomeB64: welcomeB64 ?? null,
    ratchet_tree_b64: ratchetTreeB64 ?? null,
    ratchetTreeB64: ratchetTreeB64 ?? null,
    group_id: resolvedGroupId ?? null,
    groupId: resolvedGroupId ?? null,
    group_handle: resolvedGroupHandle ?? null,
    groupHandle: resolvedGroupHandle ?? null,
    epoch: resolvedEpoch,
    current_epoch: resolvedEpoch,
    currentEpoch: resolvedEpoch,
  };

  if (resolvedMembers) {
    normalizedWelcome.members = resolvedMembers;
  }

  if (!normalizedWelcome.ratchetTreeB64) {
    try {
      console.warn('[MLS] Conversation snapshot missing ratchet tree metadata', {
        conversationId: normalizedConversationId,
        groupId: resolvedGroupId ?? groupId ?? null,
      });
    } catch {}
  }

  return {
    welcome: normalizedWelcome,
    path: 'mls-group',
    welcomeSource: 'mls-group',
    transportPath: 'mls-group',
  };
};

export const mintMlsSelfWelcome = async ({
  groupId,
  conversationId,
  identityId,
  token,
  preferredCiphersuite,
  supportedCiphersuites,
} = {}) => {
  if (!groupId) {
    throw new Error('groupId is required to mint MLS self welcome');
  }
  if (!identityId) {
    throw new Error('identityId is required to mint MLS self welcome');
  }

  const snapshotResult = await mintSelfWelcomeFromSnapshot({
    groupId,
    conversationId,
    identityId,
  });
  if (snapshotResult !== undefined) {
    return snapshotResult;
  }

  void token;
  void preferredCiphersuite;
  void supportedCiphersuites;

  return null;
};

export const reissueMlsWelcomes = async ({ groupId, identityId, keyPackageB64, token } = {}) => {
  if (!groupId) {
    throw new Error('groupId is required to reissue MLS welcomes');
  }
  if (!identityId) {
    throw new Error('identityId is required to reissue MLS welcomes');
  }

  const response = await fetchWithRetry(`/api/v1/mls/groups/${groupId}/reissue_welcomes`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { Authorization: token } : {}),
    },
    body: JSON.stringify({ identity_id: identityId, key_package_b64: keyPackageB64 }),
  });

  if (response.status === 404) {
    // Endpoint not available on this environment; treat as no-op.
    return null;
  }
  if (!response.ok) {
    throw await parseMlsApiError(response, 'Failed to reissue MLS welcomes');
  }

  const contentType = response.headers?.get?.('content-type') || '';
  if (contentType.includes('application/json')) {
    try {
      return await response.json();
    } catch {}
  }

  return null;
};

export const getGroupEpoch = async ({ groupId } = {}) => {
  if (!groupId) throw new Error('groupId is required to fetch MLS group epoch');

  const response = await fetchWithRetry(`/api/v1/mls/groups/${encodeURIComponent(String(groupId))}/epoch`);
  const payload = await parseJson(response, 'Failed to fetch MLS group epoch');
  const epoch = Number(payload?.epoch);
  return Number.isFinite(epoch) ? epoch : null;
};

export const getJoinBundle = async ({ groupId, identityId, keyPackageB64, token, reissue = false } = {}) => {
  if (!groupId) throw new Error('groupId is required');
  const response = await fetchWithRetry(`/api/v1/mls/groups/${encodeURIComponent(String(groupId))}/join_bundle`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { Authorization: token } : {}),
    },
    body: JSON.stringify({
      identity_id: identityId ?? null,
      client_id: identityId ?? null,
      key_package_b64: keyPackageB64 ?? null,
      reissue: Boolean(reissue),
    }),
  });

  if (response.status === 404) {
    // Endpoint not available on this environment; treat as no-op.
    return null;
  }

  if (!response.ok) {
    throw await parseMlsApiError(response, 'Failed to fetch MLS join bundle');
  }

  return parseJson(response, 'Failed to fetch MLS join bundle');
};

export const createMlsGroup = async ({
  conversationId,
  threadId,
  preferredGroupId,
  ratchetTreeB64,
  welcomeB64,
  members,
} = {}) => {
  const resolvedConversationId = conversationId || threadId;
  if (!resolvedConversationId) {
    throw new Error('conversationId (or threadId) is required to create MLS group');
  }

  const body = {
    conversation_id: resolvedConversationId,
  };

  if (threadId) {
    body.thread_id = threadId;
  }

  if (preferredGroupId) {
    body.preferred_group_id = preferredGroupId;
  }
  if (ratchetTreeB64) {
    body.ratchet_tree_b64 = ratchetTreeB64;
  }
  if (welcomeB64) {
    body.welcome_b64 = welcomeB64;
  }

  if (Array.isArray(members)) {
    const normalizedMembers = members
      .map((value) => {
        if (value == null) return null;
        try {
          const stringified = String(value);
          return stringified.trim() ? stringified : null;
        } catch {
          return null;
        }
      })
      .filter((value) => value != null);
    if (normalizedMembers.length > 0) {
      body.members = normalizedMembers;
    }
  }

  const response = await fetchWithRetry('/api/v1/mls/groups', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  const snapshot = normalizeServerGroupSnapshot(
    await parseJson(response, 'Failed to create MLS group'),
  );

  const cacheKey = resolveConversationKey(resolvedConversationId);
  if (cacheKey && snapshot) {
    cachedConversationGroups.set(cacheKey, {
      snapshot,
      ts: Date.now(),
    });
  }

  return snapshot;
};

export const ensureKeyPackages = async () => {
  const response = await fetchWithRetry('/api/v1/mls/key_packages');
  return parseJson(response, 'Failed to ensure MLS key packages');
};

export function ensureKeyPackagesOnce() {
  if (keyPackagesInflight) {
    return keyPackagesInflight;
  }
  keyPackagesInflight = ensureKeyPackages().finally(() => {
    keyPackagesInflight = null;
  });
  return keyPackagesInflight;
}

export const publishMlsKeyPackage = async ({
  identityId,
  clientId,
  ciphersuite,
  keyPackageB64,
  force = false,
} = {}) => {
  if (!keyPackageB64) throw new Error('keyPackageB64 is required');

  const query = force ? '?force=true' : '';
  const response = await fetchWithRetry(`/api/v1/mls/key_packages${query}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      key_package: {
        ...(identityId ? { identity_id: identityId } : {}),
        ...(clientId ? { client_id: clientId } : {}),
        ...(ciphersuite != null ? { ciphersuite } : {}),
        key_package_b64: keyPackageB64,
      },
    }),
  });

  let payload = null;
  try {
    payload = await response.clone().json();
  } catch {}

  if (response.status === 409) {
    return { status: 409, ok: true, alreadyExists: true };
  }
  if (response.status === 401) {
    const error = new Error(payload?.error?.message || payload?.message || 'Unauthorized');
    error.status = 401;
    error.code = payload?.error?.code || payload?.code || null;
    if (payload) error.payload = payload;
    throw error;
  }
  if (!response.ok) {
    const error = new Error(
      payload?.error?.message ||
        payload?.message ||
        `Failed to publish MLS key package (${response.status})`,
    );
    error.status = response.status;
    error.code = payload?.error?.code || payload?.code || null;
    if (payload) error.payload = payload;
    throw error;
  }

  if (payload != null) {
    if (payload?.enrollment?.status === 'unavailable') {
      const error = new Error('MLS device enrollment worker is unavailable');
      error.code = 'enrollment_worker_unavailable';
      error.reason = payload.enrollment.reason || null;
      error.payload = payload;
      throw error;
    }
    return payload;
  }

  return parseJson(response, 'Failed to publish MLS key package');
};

export const fetchLatestMlsKeyPackage = async ({ clientId, ciphersuite } = {}) => {
  if (!clientId) throw new Error('clientId is required');

  const params = new URLSearchParams();
  params.set('client_id', clientId);
  if (ciphersuite != null) {
    params.set('ciphersuite', ciphersuite);
  }

  let response;
  try {
    response = await fetchWithRetry(
      `/api/v1/mls/key_packages/latest?${params.toString()}`,
    );
  } catch (error) {
    if (error?.status === 404) {
      return null;
    }
    throw error;
  }

  if (response.status === 404) {
    return null;
  }

  return parseJson(response, 'Failed to fetch latest MLS key package');
};

export const getMlsDeviceIdentity = async () => {
  const response = await fetch_request('/api/v1/mls/device_identity');
  return parseJson(response, 'Failed to fetch MLS device identity');
};

export const uploadMlsWelcome = async ({
  groupId,
  payload,
  epoch,
  toUserId,
  toClientId,
  welcomeB64,
  ratchetTreeB64,
}) => {
  if (!groupId) throw new Error('groupId is required');

  let requestBody = payload;

  if (!requestBody) {
    const normalizedWelcome = typeof welcomeB64 === 'string' ? welcomeB64.trim() : '';
    if (!normalizedWelcome) {
      throw new Error('welcomeB64 is required');
    }

    requestBody = {
      epoch,
      to_user_id: toUserId,
      to_client_id: toClientId,
      welcome_b64: normalizedWelcome,
      ratchet_tree_b64: ratchetTreeB64,
    };
  }

  const response = await fetch_request(`/api/v1/mls/groups/${groupId}/welcomes`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  });

  return parseJson(response, 'Failed to upload MLS welcome');
};

export const acknowledgeMlsWelcomes = async ({ groupId, welcomeIds, clientId } = {}) => {
  if (!groupId) throw new Error('groupId is required to acknowledge welcomes');
  if (!Array.isArray(welcomeIds)) throw new Error('welcomeIds must be an array');

  const normalizedIds = Array.from(
    new Set(
      welcomeIds
        .map((value) => {
          if (value == null) return null;
          try {
            const normalized = String(value);
            return normalized.trim() ? normalized : null;
          } catch {
            return null;
          }
        })
        .filter(Boolean),
    ),
  );

  if (normalizedIds.length === 0) {
    return true;
  }

  const response = await fetch_request(`/api/v1/mls/groups/${groupId}/welcomes/ack`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ welcome_ids: normalizedIds, client_id: clientId ?? null }),
  });

  const payload = await parseJson(response, 'Failed to acknowledge MLS welcomes');
  if (payload && typeof payload === 'object') {
    const normalized = { ...payload };
    const groupState =
      normalizeServerGroupSnapshot(payload.group_state ?? payload.groupState ?? null) || null;
    normalized.group_state = groupState;
    normalized.groupState = groupState;
    return normalized;
  }
  return { acked: normalizedIds, skipped: [] };
};

export const probeMlsBootstrap = async ({
  groupId,
  senderClientId,
  identityId,
  clientMsgId,
  ciphersuite,
  epoch,
  envelopeB64,
  envelopeBytes,
  envelope,
} = {}) => {
  if (!groupId) {
    throw new Error('groupId is required to probe MLS bootstrap');
  }
  if (!senderClientId) {
    throw new Error('senderClientId is required to probe MLS bootstrap');
  }

  const resolvedClientMsgId = clientMsgId || randomUuid();

  const normalizedEnvelopeB64 =
    coerceEnvelopeB64(envelopeB64)
    || coerceEnvelopeB64(envelopeBytes)
    || coerceEnvelopeB64(envelope);

  if (!normalizedEnvelopeB64) {
    const error = new Error('MLS probe envelope is required');
    error.code = 'MLS_PROBE_ENVELOPE_UNAVAILABLE';
    error.context = 'probeMlsBootstrap';
    error.groupId = groupId;
    error.senderClientId = senderClientId;
    throw error;
  }

  const probeEpoch = epoch ?? DEFAULT_MLS_PROBE_EPOCH;
  const probeCiphersuite = ciphersuite ?? DEFAULT_MLS_PROBE_CIPHERSUITE;
  const resolvedIdentityId = identityId ?? senderClientId ?? null;

  const { groupId: headerGroupId, epoch: headerEpoch, ciphersuite: headerCiphersuite } =
    resolveMlsHeaderValues({ groupId, epoch: probeEpoch, ciphersuite: probeCiphersuite });

  const { body: payloadBody } = prepareMlsMessagePayload(
    {
      senderClientId,
      clientMsgId: resolvedClientMsgId,
      msgType: 'public_message',
      epoch: probeEpoch,
      envelopeB64: normalizedEnvelopeB64,
    },
    { context: 'probeMlsBootstrap' },
  );

  if (typeof console !== 'undefined' && typeof console.info === 'function') {
    try {
      console.info('[MLS] Probing bootstrap with sender_client_id', {
        senderClientId,
      });
    } catch {}
  }

  const retryDelays = [250, 1000, 2000];
  let attempt = 0;
  let lastBusyError = null;

  // Up to four attempts (initial + three retries with incremental backoff)
  // specifically handling 503 "Service Unavailable" responses.
  // eslint-disable-next-line no-constant-condition
  while (true) {
    let response;
    try {
      const headers = {
        'Content-Type': 'application/json',
        'X-MLS-Group-Id': headerGroupId,
      };
      if (resolvedIdentityId) {
        headers['X-MLS-Client-Id'] = String(resolvedIdentityId);
      }
      if (headerCiphersuite != null) {
        headers['X-MLS-Ciphersuite'] = headerCiphersuite;
      }
      if (headerEpoch != null) {
        headers['X-MLS-Epoch'] = headerEpoch;
      }

      response = await fetch_request(`/api/v1/mls/groups/${groupId}/messages`, {
        method: 'POST',
        headers,
        body: payloadBody,
      });
    } catch (error) {
      error.clientMsgId = resolvedClientMsgId;
      throw error;
    }

    if (response.status === 503) {
      lastBusyError = await parseMlsApiError(response, 'MLS backend busy');
      lastBusyError.clientMsgId = resolvedClientMsgId;
      if (attempt >= retryDelays.length) {
        return { busy: true, error: lastBusyError };
      }
      const delay = retryDelays[attempt];
      attempt += 1;
      await sleep(delay);
      continue;
    }

    if (response.ok) {
      return { ok: true, clientMsgId: resolvedClientMsgId };
    }

    if (response.status === 422) {
      const error = await parseMlsApiError(response, 'Invalid MLS bootstrap probe');
      error.clientMsgId = resolvedClientMsgId;

      const pendingMarker =
        error?.code === 'pending_welcomes'
        || error?.payload?.error?.code === 'pending_welcomes'
        || error?.payload?.code === 'pending_welcomes';

      if (pendingMarker) {
        return {
          pending: true,
          invalidProbe: true,
          clientMsgId: resolvedClientMsgId,
          error,
        };
      }

      throw error;
    }

    const error = await parseMlsApiError(response, 'Failed to probe MLS bootstrap');
    error.clientMsgId = resolvedClientMsgId;
    throw error;
  }
};

export const sendMlsMessage = async ({
  groupId,
  senderClientId,
  identityId,
  msgType = 'application',
  epoch,
  ciphersuite,
  envelopeB64,
  ciphertextB64,
  contentType,
  clientMsgId,
  welcomes,
  historyCiphertextB64,
  historyIvB64,
  historyAlg,
}) => {
  if (!groupId) throw new Error('groupId is required');
  if (!senderClientId) throw new Error('senderClientId is required');
  const encodedEnvelope = envelopeB64 || ciphertextB64;
  if (!encodedEnvelope) throw new Error('envelopeB64 is required');

  const messageClientId = clientMsgId || randomUuid();

  const normalizedMsgType = msgType || 'application';
  const normalizedContentType =
    contentType ?? (normalizedMsgType === 'application' ? 'application/mls' : null);

  const { body: payloadBody } = prepareMlsMessagePayload(
    {
      senderClientId,
      msgType: normalizedMsgType,
      epoch,
      envelopeB64: encodedEnvelope,
      ciphertextB64: ciphertextB64 ?? encodedEnvelope,
      contentType: normalizedContentType,
      clientMsgId: messageClientId,
      welcomes,
      historyCiphertextB64,
      historyIvB64,
      historyAlg,
    },
    { context: 'sendMlsMessage' },
  );

  if (typeof console !== 'undefined' && typeof console.info === 'function') {
    try {
      console.info('[MLS] Sending group message with sender_client_id', {
        senderClientId,
        groupId,
      });
    } catch {}
  }

  const { groupId: headerGroupId, epoch: headerEpoch, ciphersuite: headerCiphersuite } =
    resolveMlsHeaderValues({ groupId, epoch, ciphersuite });

  const resolvedIdentityId = identityId ?? senderClientId ?? null;
  const headers = {
    'Content-Type': 'application/json',
    'X-MLS-Group-Id': headerGroupId,
  };
  if (resolvedIdentityId) {
    headers['X-MLS-Client-Id'] = String(resolvedIdentityId);
  }
  if (headerCiphersuite != null) {
    headers['X-MLS-Ciphersuite'] = headerCiphersuite;
  }
  if (headerEpoch != null) {
    headers['X-MLS-Epoch'] = headerEpoch;
  }

  const response = await fetchWithRetry(`/api/v1/mls/groups/${groupId}/messages`, {
    method: 'POST',
    headers,
    body: payloadBody,
  });

  if (!response.ok) {
    throw await parseMlsApiError(response, 'Failed to send MLS message');
  }

  const result = await parseJson(response, 'Failed to send MLS message');
  const normalized =
    result && typeof result === 'object'
      ? { ...result }
      : {};
  if (!('client_msg_id' in normalized) || normalized.client_msg_id == null) {
    normalized.client_msg_id = messageClientId;
  }
  const serverSeq = normalized.server_seq ?? result?.server_seq ?? null;
  normalized.server_seq = serverSeq;
  return normalized;
};

export const fetchMlsMessages = async ({ groupId, since, clientId } = {}) => {
  if (!groupId) throw new Error('groupId is required');
  const params = new URLSearchParams();
  if (since != null && since !== '') {
    if (typeof since === 'object' && !Array.isArray(since)) {
      Object.entries(since).forEach(([cursorGroupId, cursorSeq]) => {
        if (cursorGroupId && cursorSeq != null && cursorSeq !== '') {
          params.set(`since[${cursorGroupId}]`, String(cursorSeq));
        }
      });
    } else {
      // Backend expects a per-group cursor map.
      params.set(`since[${groupId}]`, String(since));
    }
  }
  const query = params.toString();
  const headers = {};
  if (clientId) {
    headers['X-MLS-Client-Id'] = String(clientId);
  }

  const init = Object.keys(headers).length > 0 ? { headers, cache: 'no-store' } : { cache: 'no-store' };
  const response = await fetchWithRetry(
    `/api/v1/mls/groups/${groupId}/pending${query ? `?${query}` : ''}`,
    init,
  );
  const payload = await parseJson(response, 'Failed to fetch MLS messages');
  if (Array.isArray(payload)) {
    return { items: payload, next_cursor: null };
  }
  if (payload && Array.isArray(payload.items)) {
    return payload;
  }
  if (payload && Array.isArray(payload.messages)) {
    return { ...payload, items: payload.messages };
  }
  return { items: [], next_cursor: payload?.next_cursor ?? null };
};
